import { useInView } from '@/hooks/useInView';

const pillars = [
  {
    number: '01',
    title: 'Exclusivity',
    description:
      'We serve a carefully curated clientele by referral only. This ensures every relationship receives our undivided attention and every property receives bespoke care tailored to its unique character.',
  },
  {
    number: '02',
    title: 'Discretion',
    description:
      "Privacy is paramount. We conduct all matters with the utmost confidentiality, protecting both our clients' identities and their investments with rigorous security protocols and professional integrity.",
  },
  {
    number: '03',
    title: 'Excellence',
    description:
      'Good enough is never enough. We pursue perfection in every detail — from tenant placement to maintenance execution — because your property deserves nothing less than exceptional stewardship.',
  },
  {
    number: '04',
    title: 'Partnership',
    description:
      'We view every client relationship as a true partnership. Your goals become our mission. We communicate transparently, act proactively, and remain accountable for delivering results that exceed expectations.',
  },
];

const STAGGER_MS = 120;

export default function Philosophy() {
  const [headerRef, headerInView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  const [gridRef, gridInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [quoteRef, quoteInView] = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <section id="philosophy" className="w-full bg-[#2C2C2C] py-28 px-6 md:px-16">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row gap-12 md:gap-24 mb-20 transition-all duration-700 ease-out ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="md:w-1/3">
            <span className="text-xs uppercase tracking-[0.25em] text-[#8B7355] font-semibold">
              — Our Philosophy
            </span>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-tight mb-6">
              Principles That Define Us
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed font-light">
              Our philosophy is rooted in four unwavering principles that guide every decision we make and every action we take on behalf of our clients.
            </p>
          </div>
        </div>

        {/* Pillars Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
          {pillars.map((pillar, index) => (
            <div
              key={pillar.number}
              style={{
                transitionDelay: gridInView ? `${index * STAGGER_MS}ms` : '0ms',
              }}
              className={`bg-[#2C2C2C] p-10 md:p-12 transition-all duration-700 ease-out ${
                gridInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              } ${index === 0 ? 'md:border-r md:border-b border-white/10' : ''} ${
                index === 1 ? 'md:border-b border-white/10' : ''
              } ${index === 2 ? 'md:border-r border-white/10' : ''}`}
            >
              <div className="flex items-start gap-6">
                <span className="text-5xl md:text-6xl font-light text-[#8B7355]/30 leading-none">
                  {pillar.number}
                </span>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-4 tracking-wide">
                    {pillar.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed font-light">
                    {pillar.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Quote */}
        <div
          ref={quoteRef}
          className={`mt-20 pt-12 border-t border-white/10 text-center transition-all duration-900 ease-out ${
            quoteInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDuration: '900ms' }}
        >
          <blockquote className="text-xl md:text-2xl text-white/80 font-light italic leading-relaxed max-w-3xl mx-auto">
            &ldquo;We do not seek to be the largest property management firm. We seek to be the firm that discerning owners trust implicitly.&rdquo;
          </blockquote>
          <p className="mt-6 text-sm text-[#8B7355] uppercase tracking-[0.2em]">
            — The Higher Standard Global Commitment
          </p>
        </div>

      </div>
    </section>
  );
}